import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import QuotationStatusBadge from '#/modules/crmMgmt/quotation/components/QuotationStatusBadge';
import {
  formatQuotationCustomer,
  formatQuotationDecimal,
  formatQuotationRoundingMode,
  formatQuotationTotal,
} from '#/modules/crmMgmt/quotation/helper/quotationFormat';
import { formatDateOrDash, formatDateTimeOrDash } from '#/utils/date';

function InfoField({ label, value, wide = false }) {
  return (
    <div className={cn(wide && 'sm:col-span-2 md:col-span-2')}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">{value}</p>
    </div>
  );
}

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  wide: PropTypes.bool,
};

/** 報價單表頭：單號、狀態、客戶與聯絡快照、付款條件、取整、金額摘要（小計／5% 稅／總額）與內部備註。 */
const QuotationMainInfoCard = ({ detail, actions = null, compact = false }) => (
  <div className="receipt-card-elevate">
    <div className={cn('receipt-card-surface', compact ? 'p-4' : 'p-5')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn('font-bold text-gray-900', compact ? 'text-xl' : 'text-2xl')}>
              {detail.quotationNo || '-'}
            </h1>
            {detail.status ? <QuotationStatusBadge status={detail.status} /> : null}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            客戶：
            {formatQuotationCustomer(detail.customer)}
          </p>
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {actions}
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
          !compact && 'sm:grid-cols-2 md:grid-cols-4',
        )}
      >
        <InfoField label="聯絡人" value={detail.contactName || '-'} />
        <InfoField label="聯絡電話" value={detail.contactPhone || '-'} />
        <InfoField label="客戶地址" value={detail.customerAddress || '-'} wide />
        <InfoField label="付款條件" value={detail.paymentTermName || '-'} />
        <InfoField label="有效截止日" value={formatDateOrDash(detail.validUntil)} />
        <InfoField label="發出時間" value={formatDateTimeOrDash(detail.issuedAt)} />
        <InfoField label="總額取整" value={formatQuotationRoundingMode(detail.roundingMode)} />
        <InfoField label="主項目數" value={String((detail.lines ?? []).length)} />
        <InfoField label="小計" value={formatQuotationDecimal(detail.amountSummary?.subtotalAmount)} />
        <InfoField label="稅金（5%）" value={formatQuotationDecimal(detail.amountSummary?.taxAmount)} />
        <InfoField label="總額（含稅）" value={formatQuotationTotal(detail.amountSummary?.totalAmount)} />
        <InfoField label="建立" value={formatDateTimeOrDash(detail.record?.createdAt)} />
        <InfoField label="修改" value={formatDateTimeOrDash(detail.record?.updatedAt)} />
        <InfoField label="內部備註" value={detail.remark || '-'} wide />
      </div>
    </div>
  </div>
);

QuotationMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  actions: PropTypes.node,
  compact: PropTypes.bool,
};

export default QuotationMainInfoCard;
